import { ordersLexicon } from "./lexicon";
import {
  DECIMAL_WORD,
  digitsToWords,
  numberToWords,
  spellLetters,
} from "./numbers";
import {
  pluralOfSpoken,
  saysTwice,
  withoutListLabel,
  withoutSaysTwiceMarker,
  withoutSeparatorRuns,
} from "./spoken-stream";
import type {
  CriticalKind,
  NormalizeSpeechOptions,
  SpeechNormalization,
  UnresolvedTokenLogEntry,
  UnresolvedTokenOccurrence,
} from "./types";

const PHONETIC: Readonly<Record<string, string>> = Object.freeze({
  A: "alpha",
  B: "bravo",
  C: "charlie",
  D: "delta",
  E: "echo",
  F: "foxtrot",
  G: "golf",
  H: "hotel",
  I: "india",
  J: "juliett",
  K: "kilo",
  L: "lima",
  M: "mike",
  N: "november",
  O: "oscar",
  P: "papa",
  Q: "quebec",
  R: "romeo",
  S: "sierra",
  T: "tango",
  U: "uniform",
  V: "victor",
  W: "whiskey",
  X: "x-ray",
  Y: "yankee",
  Z: "zulu",
});

const PURE_EQUIPMENT = new Set(["BTR", "BRDM", "BRM", "MT-LB", "BMP", "PKM"]);

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * A word-bounded match for one lexicon entry.
 *
 * **The boundary counts letters, not ASCII letters.** Written with
 * `[A-Za-z0-9_]`, the lookahead let an entry match where the next character
 * was a letter outside ASCII: the cards write "Scheme of Manœuvre", and an
 * entry for "Scheme of Man" matched it and left the ligature and the rest of
 * the word stranded. Found 17 October 2026 (c) while adding that entry under
 * Ruling 94.
 */
function tokenRegex(token: string): RegExp {
  const escaped = escapeRegex(token);
  return new RegExp(`(?<![\\p{L}\\p{N}_])${escaped}(?![\\p{L}\\p{N}_])`, "gu");
}

/**
 * Equipment designator prefixes said as a word rather than as letters.
 *
 * Ruling 94: "'BREM-2' is 'B R E M two'. 'GAZ-66' is 'GAZ sixty-six'." The
 * difference is not in the shape of the designator, so it cannot be derived —
 * it is a list, and this is the list.
 */
const WORD_PREFIX = new Set(["GAZ"]);

export function normalizeEquipmentDesignator(value: string): string | null {
  const numbered = /^([A-Z]+)-(\d+)([A-Z]?)$/.exec(value);
  if (numbered !== null) {
    /* v8 ignore next -- these captures are mandatory in the guarding regex */
    const letters = numbered[1] ?? "";
    /* v8 ignore next -- these captures are mandatory in the guarding regex */
    const number = numbered[2] ?? "";
    /* v8 ignore next -- the optional capture returns an empty string */
    const trailingLetter = numbered[3] ?? "";
    const trailing = trailingLetter === "" ? "" : ` ${trailingLetter}`;
    const prefix = WORD_PREFIX.has(letters) ? letters : spellLetters(letters);
    return `${prefix} ${numberToWords(Number(number))}${trailing}`;
  }
  if (!PURE_EQUIPMENT.has(value)) return null;
  return spellLetters(value);
}

function equipment(text: string): string {
  return text.replace(
    /\b(?:[A-Z]{1,6}-\d+[A-Z]?|BTR|BRDM|BRM|MT-LB|BMP|PKM)\b/g,
    (token) => {
      const spoken = normalizeEquipmentDesignator(token);
      /* v8 ignore next -- the regex accepts only forms handled above */
      return spoken ?? token;
    },
  );
}

/**
 * Suppresses a grid zone designator from the spoken stream.
 *
 * Ruling of 12 September 2026. A designator immediately before a grid label
 * speaks as three words in the same shape doc 03 section 5 gives callsigns,
 * at the moment the trainee is writing down a grid. It carries nothing he
 * needs: a squadron operating inside one map sheet never uses it, and it is
 * map-administrative rather than tactical.
 *
 * Only the spoken stream is affected. The display pane stays source-faithful.
 * The designator is recognised only where it sits directly before a grid
 * label, so a callsign of the same shape is never touched.
 */
function gridZoneDesignators(text: string): string {
  return text.replace(/\b\d{1,2}[A-HJ-NP-Z]\s+(?=(?:GR|grid)\b)/gi, "");
}

function grids(text: string): string {
  return text.replace(
    /\b(?:GR|grid)\s*[:#-]?\s+(\d+(?:\s+\d+)*)\b/gi,
    (_match, groups: string) => {
      const spokenGroups = groups.split(/\s+/).map(digitsToWords);
      return `grid, ${spokenGroups.join(", ")}`;
    },
  );
}

/**
 * A frequency, as the cards write it: two or three figures, a decimal point,
 * three figures.
 *
 * The one shape, exported so that nothing writes it out a second time. The
 * net frequency line is the only place in this material that carries one, and
 * `said-twice` must bind the same thing the reader says.
 */
export const FREQUENCY = /\b(\d{2,3})\.(\d{3})\b/g;

/**
 * A frequency is said digit by digit, and its point is said "decimal".
 *
 * **Ruling 95**, 18 October 2026 (a), on the open item left by Ruling 92:
 * "A frequency is said digit by digit, and its decimal point is said
 * 'decimal'. Nine is 'niner'." His source for digit by digit is
 * `ato_communications.md` section 2.6, the DP1 ATL communications lesson,
 * slide 11 — numbers are sent digit by digit — and the word "decimal" is his
 * call, to be corrected by what the trainee hears.
 *
 * It runs before `callsigns`, which is what the figures would otherwise meet:
 * the whole part of a frequency is two figures and reads exactly like a
 * callsign. That lookahead stays, because it is what leaves a decimal whole
 * for this rule to find, and it still carries any decimal this shape does not
 * match.
 */
function frequencies(text: string): string {
  return text.replace(
    FREQUENCY,
    (_match, whole: string, fraction: string) =>
      `${digitsToWords(whole)} ${DECIMAL_WORD} ${digitsToWords(fraction)}`,
  );
}

/**
 * Two figures, and a phonetic for a trailing letter: a callsign.
 *
 * **The whole part of a decimal is not a callsign.** Found 17 October 2026 (c)
 * while fixing Ruling 92's detector: a net frequency had the two figures in
 * front of its point read as a callsign, so the reader said two digit words
 * and then the three figures after the point as written, and nothing
 * downstream could see a frequency at all. The lookahead leaves a decimal
 * whole.
 */
function callsigns(text: string): string {
  return text.replace(
    /\b(\d{2})([A-Z]?)\b(?!\.\d)/g,
    (_match, digits: string, letter: string) => {
      /* v8 ignore next -- the regex restricts letters to the complete A-Z map */
      const trailing = letter === "" ? "" : ` ${PHONETIC[letter] ?? letter}`;
      return `${digitsToWords(digits)}${trailing}`;
    },
  );
}

function unitDesignators(text: string): string {
  return text.replace(
    /\b(\d+)\s+(PPCLI|RCR|CMBG|BTG|Armd)\b/g,
    (_match, digits: string, unit: string) => {
      const spokenUnit = unit === "Armd" ? "armoured" : spellLetters(unit);
      return `${digitsToWords(digits)} ${spokenUnit}`;
    },
  );
}

function phasesDurationsAndBearings(text: string): string {
  return text
    .replace(
      /\bPh\s+(\d+)\b/g,
      (_match, number: string) => `phase ${numberToWords(Number(number))}`,
    )
    .replace(
      /\b(\d{4})\s+hrs\b/gi,
      (_match, digits: string) => `${digitsToWords(digits)} hours`,
    )
    .replace(
      /\b(\d+)\s*(hrs?|hours?|mins?|minutes?)\b/gi,
      (_match, number: string, unit: string) => {
        const normalizedUnit = /^h/i.test(unit) ? "hours" : "minutes";
        return `${numberToWords(Number(number))} ${normalizedUnit}`;
      },
    )
    .replace(
      /\b(\d{1,4})\s+mils\b/gi,
      (_match, digits: string) => `${digitsToWords(digits)} mils`,
    );
}

/** The roman numerals a supply class takes. */
const CLASS_NUMERALS: Readonly<Record<string, string>> = Object.freeze({
  I: "one",
  II: "two",
  III: "three",
  IV: "four",
  V: "five",
});

const CLASS_RUN =
  /\bClass\b(\s+(?:I{1,3}|IV|V)\b(?:\s*(?:,|and|\/)\s*(?:I{1,3}|IV|V)\b)*)/g;

/**
 * A roman numeral after "Class" is said as a number.
 *
 * Ruling 94: "A roman numeral after 'Class' is said as a number: 'Class III'
 * is 'class three'." A rule rather than lexicon entries, because the cards
 * write the list three ways — "Class I", "Class I, III and V" and
 * "Class I, III, V" — and a table of written forms covers only the ones
 * somebody thought to write down. That was the defect behind the old entry
 * `Class I / III / V`, a written form no card uses.
 */
function classNumerals(text: string): string {
  return text.replace(
    CLASS_RUN,
    (_match, run: string) =>
      `class${run.replace(
        /\b(I{1,3}|IV|V)\b/g,
        (numeral) =>
          /* v8 ignore next -- the pattern admits only the five keys */
          CLASS_NUMERALS[numeral] ?? numeral,
      )}`,
  );
}

/**
 * Every expansion the reader has, written and derived.
 *
 * The derived plurals are the block's own rule applied to the entries it
 * already holds; an entry written out explicitly always wins over the
 * plural derived from its singular.
 */
export function lexiconExpansions(): readonly {
  readonly spoken: string;
  readonly written: string;
}[] {
  const written = new Set(ordersLexicon.entries.map((entry) => entry.written));
  const derived: { spoken: string; written: string }[] = [];
  for (const entry of ordersLexicon.entries) {
    const plural = pluralOfSpoken(entry.spoken);
    if (plural === null) continue;
    const pluralWritten = `${entry.written}s`;
    if (written.has(pluralWritten)) continue;
    derived.push({ spoken: plural, written: pluralWritten });
  }
  return [...ordersLexicon.entries, ...derived];
}

function applyLexicon(text: string): string {
  const entries = [...lexiconExpansions()].sort(
    (left, right) => right.written.length - left.written.length,
  );
  return entries.reduce(
    (current, entry) =>
      current.replace(tokenRegex(entry.written), entry.spoken),
    text,
  );
}

/**
 * How an unresolved token is spoken: §4.1, letters as letters, figures as
 * figures, and a mixed-case token left exactly as written.
 *
 * Exported for its own test. Ruling 94 took `Spec Fire` out of the unresolved
 * list by giving it an expansion, and it was the only mixed-case token there,
 * so the rule for mixed case had no caller left to exercise it. A rule with no
 * test is a rule that is true until someone adds a token.
 */
export function speakUnresolved(token: string): string {
  if (/^[A-Z]+-\d+[A-Z]?$/.test(token)) {
    const spoken = normalizeEquipmentDesignator(token);
    /* v8 ignore next -- the guarding regex is the equipment parser's grammar */
    return spoken ?? token;
  }
  if (/[a-z]/.test(token)) {
    return token;
  }
  return token
    .split(" ")
    .map((part) =>
      Array.from(part)
        .map((character) =>
          /\d/.test(character) ? digitsToWords(character) : character,
        )
        .join(" "),
    )
    .join(" ");
}

function unresolved(
  text: string,
  nodeId: string,
): {
  readonly text: string;
  readonly occurrences: readonly UnresolvedTokenOccurrence[];
  readonly logEntries: readonly UnresolvedTokenLogEntry[];
} {
  const occurrences: UnresolvedTokenOccurrence[] = [];
  const alternatives = [...ordersLexicon.unresolvedTokens]
    .sort((left, right) => right.length - left.length)
    .map(escapeRegex)
    .join("|");
  const pattern = new RegExp(
    `(?<![A-Za-z0-9_])(?:${alternatives}|[A-Z]{1,6}-[0-9]+[A-Z]?)(?![A-Za-z0-9_])`,
    "g",
  );
  const current = text.replace(pattern, (token) => {
    const equipmentMatch = /^([A-Z]+)-\d+[A-Z]?$/.exec(token);
    if (
      equipmentMatch?.[1] !== undefined &&
      ordersLexicon.equipmentDesignators.includes(equipmentMatch[1])
    ) {
      return token;
    }
    const spokenText = speakUnresolved(token);
    occurrences.push({
      id: `${nodeId}/unresolved-${String(occurrences.length + 1).padStart(2, "0")}`,
      nodeId,
      token,
      spokenText,
      blocksPlayback: false,
    });
    return spokenText;
  });
  return {
    text: current,
    occurrences,
    logEntries: occurrences.map(({ nodeId: occurrenceNodeId, token }) => ({
      schemaVersion: 1,
      action: "unresolved-token",
      nodeId: occurrenceNodeId,
      token,
    })),
  };
}

function criticalKinds(displayText: string): readonly CriticalKind[] {
  const kinds: CriticalKind[] = [];
  if (/\b(?:GR|grid)\b/i.test(displayText)) kinds.push("grid");
  if (/\b\d{2}[A-Z]\b|chain of command|\bCoC\b/i.test(displayText)) {
    kinds.push("callsign");
  }
  if (/\b(?:IFF|CoC|priority of targets?)\b/i.test(displayText)) {
    kinds.push("coordination");
  }
  if (/\b(?:MHz|kHz|frequency)\b/i.test(displayText)) kinds.push("frequency");
  if (/\b(?:code ?word|password)\b/i.test(displayText)) kinds.push("code-word");
  return kinds;
}

export function normalizeSpeechText(
  displayText: string,
  options: NormalizeSpeechOptions = {},
): SpeechNormalization {
  const nodeId = options.nodeId ?? "text";
  // Ruling 82 come first: they
  // decide what there is to say before anything decides how to say it. A
  // separator run can empty the text entirely, and an emptied text yields no
  // segment, which is what "Say nothing" means.
  const marked = saysTwice(displayText);
  const source = withoutListLabel(
    withoutSeparatorRuns(withoutSaysTwiceMarker(displayText)),
  );
  const unresolvedResult = unresolved(source, nodeId);
  const spokenText = applyLexicon(
    classNumerals(
      phasesDurationsAndBearings(
        unitDesignators(
          callsigns(
            frequencies(
              grids(gridZoneDesignators(equipment(unresolvedResult.text))),
            ),
          ),
        ),
      ),
    ),
  );
  return {
    displayText,
    spokenText,
    saysTwice: marked,
    unresolvedTokens: unresolvedResult.occurrences,
    logEntries: unresolvedResult.logEntries,
    criticalKinds: criticalKinds(displayText),
  };
}
